import { useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { StoreContext, namespace, ActionTypeValues } from '@/context';
import { PromiseState } from '@/constants';
import { IThreadLisItem } from '@/api/thread/list';
import Toast from '@/components/Toast';
import DefaultAvatar from '../../assets/icon/account/def_avatar.webp';
import { getUserLoginInfo, IUserLoginInfo, ACCOUNT_ROLE } from '@/api/user';

// 页面专属 hooks / 渲染器 / 类型
import useSls from '@/hooks/useSls';
import useUserCenterSlsReport from '@/features/user-center/useUserCenterSlsReport';
import { parseUidFromQuery, resolveUserCenterCreditTip } from '@/utils/userCenter';
import { getUserCenterPcServerSideProps } from '@/features/user-center/pc/ssr';
import { USER_CENTER_MAX_POINTS_VALUE } from '@/features/user-center/pc/constants';
import { isPcTabContentEmpty, renderPcTabContent } from '@/features/user-center/pc/renderers';
import UserCenterPcLayout from './pc/PageLayout';
import { useUserCenterPcListLogic } from '@/features/user-center/pc/useUserCenterPcListLogic';
import { useUserCenterPcActions } from '@/features/user-center/pc/useUserCenterPcActions';
import { IUserCenterProps } from '@/features/user-center/pc/types';

const UserCenter = ({ initialWorks = [], userStats, initialAccount, initialUserInfo, initialDevices = [], navList, saltkey, initialSsrDuration = 0, initialLoginInfoSls = null }: IUserCenterProps) => {
  // 页面级上下文与全局状态入口：i18n、路由、全局 store、埋点与日志上报
  const t = useTranslations();
  const { state, dispatch } = useContext(StoreContext);
  const router = useRouter();
  // 全局 store 中挂载了 account/sensors/permission 等页面运行依赖
  const { account: storeAccount, sensors, permission } = state[namespace.app];
  // 神策埋点能力 
  const { track, TRACK_EVENTS } = sensors || {};
  // SLS 上报能力
  const { reportSlsData } = useSls();

  // UI 与交互状态：tab、各弹窗、资料 patch、本地可见性开关
  const [activeTab, setActiveTab] = useState('myPosts');
  const [isTabInitializedFromQuery, setIsTabInitializedFromQuery] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [showPointsExchangeModal, setShowPointsExchangeModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profilePatch, setProfilePatch] = useState<{ name?: string; avatarUrl?: string } | null>(null);
  const [badgeIconError, setBadgeIconError] = useState(false);
  const [hideDistance, setHideDistance] = useState(false);
  const [updating, setUpdating] = useState(false);
  // 只提示一次积分 toast，避免重复弹出干扰体验
  const hasShownCreditTipRef = useRef(false);
  // 每个 tab 的全局滚动位置快照，key 为 tab key，value 为 scrollY
  const tabScrollYMapRef = useRef<Record<string, number>>({});
  // 切换 Tab 前记录的 scrollY。
  // 为什么需要它：Tab 切换触发 React 重渲染后，display:none/block 会导致文档高度突变，
  // 浏览器会自动将 scrollY clamp 到新的 maxScrollTop。当 restoreTabScrollY 在 useEffect 中执行时，
  // 从 scrollElement.scrollTop 读到的已经是被 clamp 后的值，不再是切换前的真实位置。
  // 所以需要在 saveTabScrollY（DOM 更新前同步执行）中提前记录。
  const scrollYBeforeSwitchRef = useRef<number>(0);
  // 帖子局部状态 patch：点赞/收藏等本地修改先写到这里，再叠加到列表
  const threadStatePatchRef = useRef<Record<string, Partial<IThreadLisItem>>>({});
  // 账户信息优先级：先用 store 的已完成态，再回退 SSR 注入数据
  const account = useMemo(() => {
    if (storeAccount?.status === PromiseState.FULFILLED) return storeAccount as IUserLoginInfo;
    return (initialAccount as IUserLoginInfo) || storeAccount;
  }, [storeAccount, initialAccount]);
  // URL 中 uid 标准化，后续统一用于"本人/访客视角"判定
  const routeUid = useMemo(() => parseUidFromQuery(router.query.uid), [router.query.uid]);
  // 本人/访客视角判定规则：
  // 1) URL 有 uid 时：登录 id 与 uid 相等即本人
  // 2) URL 无 uid 时：回退与 initialUserInfo.id 对比
  // 3) 都不可用时：默认本人（兼容历史入口）
  const isSelfView = useMemo(() => {
    const loginId = Number(account?.id);
    if (!Number.isFinite(loginId)) return false;
    if (Number.isFinite(routeUid)) {
      return loginId === routeUid;
    }
    if (Number.isFinite(Number(initialUserInfo?.id))) {
      return String(loginId) === String(initialUserInfo?.id);
    }
    return true;
  }, [account?.id, routeUid, initialUserInfo?.id]);

  // 禁用浏览器自动滚动恢复。
  // 浏览器在前进/后退时会自动恢复 scrollY，但本页面的 Tab 切换有自己的滚动记忆逻辑，
  // 两者会冲突，所以设为 manual 由页面自行管理。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      // 清理保留（未来扩展用）
    };
  }, []);

  /**
   * 获取吸顶临界点（pinStartY）。
   *
   * 页面布局中，Sidebar 和灰色遮挡条使用 CSS sticky 定位（top: 80px / 64px）。
   * 当 scrollY 达到 pinStartY 时，contentBody 的顶部刚好贴到 sticky top 的位置，
   * 即 Sidebar 和遮挡条开始"粘住"，视觉上进入吸顶状态。
   *
   * 计算方式：contentBody 在文档中的绝对顶部位置 - 80px（sticky top 值）。
   */
  const getPinStartY = useCallback(() => {
    if (typeof window === 'undefined') return 0;
    const scrollElement = document.getElementById('__next') || document.documentElement;
    const contentBody = document.querySelector('[class*="contentBody"]') as HTMLElement | null;
    if (!contentBody) return 0;
    const contentBodyTop = contentBody.getBoundingClientRect().top + scrollElement.scrollTop;
    return Math.max(0, contentBodyTop - 80);
  }, []);

  /**
   * 保存当前 Tab 的滚动位置，并预设目标 Tab 的 scrollY。
   *
   * 这个函数在 setActiveTab 之前同步调用（即 DOM 更新前），
   * 目的是在 display:none/block 切换导致文档高度突变之前，就把 scrollY 设好。
   *
   * 预设策略（避免切换时画面跳跃）：
   * - 目标 Tab 从未访问过 + 当前已吸顶 → 预设到 pinStartY（保持吸顶，列表从头显示）
   * - 目标 Tab 从未访问过 + 当前未吸顶 → 保持当前位置不动
   * - 已吸顶 → 目标未吸顶（有记录）→ 预设到 pinStartY（保持吸顶，列表从头显示）
   * - 未吸顶 → 未吸顶（有记录）→ 保持当前位置不动
   * - 涉及动画的场景（未吸顶→已吸顶、已吸顶→已吸顶）→ 不在此处预设，留给 restoreTabScrollY 处理
   *
   * @param currentTabKey - 当前离开的 Tab
   * @param targetTabKey - 即将切换到的目标 Tab（可选，不传则只保存当前位置）
   */
  const saveTabScrollY = useCallback((currentTabKey: string, targetTabKey?: string) => {
    if (typeof window === 'undefined') return;
    const scrollElement = document.getElementById('__next') || document.documentElement;
    const y = scrollElement.scrollTop || window.scrollY;
    tabScrollYMapRef.current[currentTabKey] = y;
    scrollYBeforeSwitchRef.current = y;

    // 保存/预设目标 tab 的记录值到 map 中。
    // 同时，如果当前已吸顶，将 scrollTop 预设到 pinStartY：
    // 因为 pinStartY 在任何 tab 的内容高度下都是有效值（吸顶临界点），
    // 可以防止 display:none/block 切换后浏览器将 scrollTop clamp 到错误位置导致的画面跳动。
    if (targetTabKey) {
      const targetSavedY = tabScrollYMapRef.current[targetTabKey];
      const pinStartY = getPinStartY();
      const wasPinned = y >= pinStartY - 1;

      if (targetSavedY === undefined) {
        const presetY = wasPinned ? pinStartY : y;
        tabScrollYMapRef.current[targetTabKey] = presetY;
        if (wasPinned) {
          scrollElement.scrollTop = pinStartY;
        }
      } else {
        const targetIsPinned = targetSavedY >= pinStartY - 1;
        if (wasPinned && !targetIsPinned) {
          tabScrollYMapRef.current[targetTabKey] = pinStartY;
          scrollElement.scrollTop = pinStartY;
        } else if (wasPinned && targetIsPinned) {
          // 已吸顶 → 已吸顶：都 > pinStartY，直接跳到目标位置不会有跳动风险
          scrollElement.scrollTop = targetSavedY;
        }
        // 未吸顶的任何场景：不修改 scrollTop，由 restoreTabScrollY 处理
      }
    }
  }, [getPinStartY]);

  /**
   * 恢复目标 Tab 的全局滚动位置（异步，在 useEffect 中调用）。
   *
   * saveTabScrollY 只保存每个 tab 的 scrollY 记录，不修改 scrollTop。
   * 所有滚动恢复统一在此处理。
   *
   * 使用多帧重试机制确保在文档高度稳定（数据已渲染）后才执行恢复，
   * 避免因 display:none/block 切换后文档高度变化 + 懒加载异步时序
   * 导致的 scrollTop 被浏览器 clamp 到错误值。
   *
   * 使用 scrollYBeforeSwitchRef 而非 scrollElement.scrollTop 来判断切换前状态，
   * 因为此时 DOM 已更新（display 已切换），scrollTop 可能已被浏览器 clamp。
   */
  const restoreTabScrollY = useCallback((tabKey: string) => {
    if (typeof window === 'undefined') return;
    const savedY = tabScrollYMapRef.current[tabKey];
    if (savedY === undefined) return;
    const scrollElement = document.getElementById('__next') || document.documentElement;
    const beforeY = scrollYBeforeSwitchRef.current;
    const pinStartY = getPinStartY();
    const wasPinned = beforeY >= pinStartY - 1;
    const targetIsPinned = savedY >= pinStartY - 1;

    // 计算目标位置
    let targetY = savedY;
    if (targetIsPinned) {
      // 目标 tab 应该吸顶：至少到 pinStartY
      targetY = Math.max(savedY, pinStartY);
    }

    let attempts = 0;
    const maxAttempts = 20;

    const doRestore = () => {
      const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
      const clampedTarget = Math.min(targetY, maxScroll);

      if (maxScroll >= targetY || attempts >= maxAttempts) {
        // 文档高度已足够或达到重试上限 → 执行恢复

        if (!wasPinned && !targetIsPinned) {
          // 未吸顶 → 未吸顶：保持当前位置不动，不修改 scrollTop
          return;
        }

        if (!wasPinned && targetIsPinned) {
          // 未吸顶 → 已吸顶：平滑动画过渡
          scrollElement.scrollTo({ top: clampedTarget, behavior: 'smooth' });
          return;
        }

        // 已吸顶 → 任何目标：直接设置
        scrollElement.scrollTop = clampedTarget;
        return;
      }

      attempts++;
      setTimeout(() => requestAnimationFrame(doRestore), attempts * 60);
    };

    requestAnimationFrame(doRestore);
  }, [getPinStartY]);

  // URL 缺失 uid/uuid 时补齐，保证当前页参数完整且可分享
  useEffect(() => {
    if (!router.isReady) return;
    const uid = account?.id;
    const uuid = account?.uuid;
    if (!uid || !uuid) return;

    const currentUid = Array.isArray(router.query.uid) ? router.query.uid[0] : router.query.uid;
    if (currentUid) return;

    const internalPathname = router.pathname || '/user-center';
    const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;
    router.replace({
      pathname: internalPathname,
      query: {
        ...router.query,
        uid: String(uid),
        uuid: String(uuid),
      },
    }, {
      pathname: externalPathname,
      query: {
        ...router.query,
        uid: String(uid),
        uuid: String(uuid),
      },
    }, { shallow: true });
  }, [router, router.isReady, router.query, account?.id, account?.uuid]);

  // 解析 URL 参数并设置 activeTab：
  // do=thread/likes/favorite/mynotice 映射到对应 tab
  // ac=setting 直接拉起编辑资料弹窗
  useEffect(() => {
    if (!router.isReady) return;
    // 非本人页不跟随 do/ac 切换 tab，避免误聚焦
    if (!isSelfView) {
      setIsTabInitializedFromQuery(true);
      return;
    }

    const { do: action, ac } = router.query;
    const actionValue = Array.isArray(action) ? action[0] : action;
    const acValue = Array.isArray(ac) ? ac[0] : ac;
    
    let newTab: string | null = null;

    if (acValue === 'setting') {
      setShowEditProfileModal(true);
      const newQuery = { ...router.query };
      delete newQuery.ac;
      const internalPathname = router.pathname || '/user-center';
      const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;
      router.replace({ pathname: internalPathname, query: newQuery }, { pathname: externalPathname, query: newQuery }, { shallow: true });
    } else if (actionValue === 'thread') {
      newTab = 'myPosts';
    } else if (actionValue === 'likes') {
      newTab = 'myLikes';
    } else if (actionValue === 'favorite') {
      newTab = 'myCollections';
    } else if (actionValue === 'reply') {
      newTab = 'myReplies';
    } else if (actionValue === 'mynotice') {
      newTab = 'messages';
    }
    
    // tab 切换前先记录旧 tab 滚动位置并预设目标位置，便于回来时复位
    if (newTab) {
      setActiveTab((prevTab) => {
        if (newTab !== prevTab) {
          saveTabScrollY(prevTab, newTab as string);
          return newTab as string;
        }
        return prevTab;
      });
    }
    setIsTabInitializedFromQuery(true);
  }, [router.isReady, isSelfView, saveTabScrollY, router.query?.do, router.query?.ac]);

  // tab 变化回写 URL（do 参数），并保持 shallow + scroll:false，避免回到顶部
  // 注意：不依赖 router 对象，避免 shallow push 后 router 引用变化导致与
  // 上面的 URL→tab 解析 useEffect 之间产生时序竞争（setActiveTab 异步生效，
  // 但本 effect 读到旧 activeTab 会错误地认为 URL 不同步，从而再次 push，
  // 形成无限循环）。
  // 使用 ref 追踪最新 router.query.do，确保读取的是最新 URL 状态而非旧闭包
  const latestQueryDoRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!router.isReady) return;
    const { do: action } = router.query;
    latestQueryDoRef.current = Array.isArray(action) ? action[0] : action;
  }, [router.isReady, router.query?.do]);

  useEffect(() => {
    if (!router.isReady || !isSelfView || !isTabInitializedFromQuery) return;

    const currentQuery = { ...router.query };
    const hasAc = Object.prototype.hasOwnProperty.call(currentQuery, 'ac');
    delete currentQuery.ac;
    let newAction = '';

    switch (activeTab) {
      case 'myPosts':
        newAction = 'thread';
        break;
      case 'myLikes':
        newAction = 'likes';
        break;
      case 'myCollections':
        newAction = 'favorite';
        break;
      case 'myReplies':
        newAction = 'reply';
        break;
      case 'messages':
        newAction = 'mynotice';
        break;
      default:
        break;
    }

    const currentDo = latestQueryDoRef.current;
    const shouldUpdateDo = Boolean(newAction) && currentDo !== newAction;
    const internalPathname = router.pathname || '/user-center';
    const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;

    if (shouldUpdateDo) {
      const newQuery = { ...currentQuery, do: newAction };
      router.push({ pathname: internalPathname, query: newQuery }, { pathname: externalPathname, query: newQuery }, { shallow: true, scroll: false });
      return;
    }

    if (hasAc) {
      router.replace({ pathname: internalPathname, query: currentQuery }, { pathname: externalPathname, query: currentQuery }, { shallow: true, scroll: false });
    }
  }, [activeTab, router.isReady, isSelfView, isTabInitializedFromQuery]);

  // Tab 切换后恢复全局滚动位置（延迟一帧执行，给新 tab 内容渲染留出时间）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = requestAnimationFrame(() => {
      restoreTabScrollY(activeTab);
    });
    return () => cancelAnimationFrame(id);
  }, [activeTab, restoreTabScrollY]);

  // 展示态账户对象：统一融合 SSR 数据、store 数据与 profilePatch
  const displayAccount = useMemo(() => {
    // 无 initialUserInfo 时，使用登录态兜底（通常是本人页）
    if (!initialUserInfo) {
      const acc = (account as any) || {};
      const accAvatar = profilePatch?.avatarUrl || acc?.avatar?.large || DefaultAvatar;
      return {
        ...acc,
        name: profilePatch?.name || acc?.name,
        avatar: {
          large: accAvatar,
          medium: accAvatar,
          small: accAvatar,
        }
      };
    }

    // 有 initialUserInfo 时，以其为主并注入 patch
    const base = isSelfView ? (account as any) : {};

    const avatarUrl = profilePatch?.avatarUrl || initialUserInfo.avatar || (isSelfView ? (account as any)?.avatar?.large : "") || DefaultAvatar;
    const mergedAvatar = {
      large: avatarUrl,
      medium: avatarUrl,
      small: avatarUrl,
    };
    return {
      ...base,
      id: initialUserInfo.id,
      uuid: initialUserInfo.uuid,
      name: profilePatch?.name || initialUserInfo.name,
      avatar: mergedAvatar,
      credits: initialUserInfo.credits || 0,
      devices: initialDevices || [],
      deviceCount: initialDevices?.length || 0,
      group: initialUserInfo?.group,
      flightDistance: initialUserInfo.total_distance || 0,
      hide_distance: initialUserInfo.hide_distance,
      admin_view: initialUserInfo.admin_view,
      rank: initialUserInfo.rank,
    };
  }, [account, initialUserInfo, initialDevices, isSelfView, profilePatch]);

  // 列表域逻辑下沉：分页、合并、去重、消息同步、列表 patch
  const {
    postsData,
    postsLoading,
    postsHasMore,
    likesData,
    likesLoading,
    likesHasMore,
    likesInitialized,
    collectionsData,
    collectionsLoading,
    collectionsHasMore,
    collectionsInitialized,
    repliesData,
    repliesLoading,
    repliesInitialized,
    messagesData,
    messagesLoading,
    messagesInitialized,
    drafts,
    publishedPosts,
    showDrafts,
    showPosts,
    handleItemUpdate,
  } = useUserCenterPcListLogic({
    initialWorks,
    initialUserInfo,
    account,
    userStats,
    displayAccount,
    isSelfView,
    activeTab,
    dispatch,
    threadStatePatchRef,
  });

  // 兜底权限：本人主页不可访问时重定向到拒绝页
  useEffect(() => {
    if (account && account.home_page_accessible_by_self === false) {
      window.location.href = '/pro/access-denied';
    }
  }, [account]);

  // 管理员视角标记
  const isAdminView = useMemo(() => {
    return (account as any)?.role === ACCOUNT_ROLE.ADMIN;
  }, [account]);

  // 页面级 SLS：SSR 耗时 / 登录信息 API 关键指标上报
  useUserCenterSlsReport({
    reportSlsData,
    initialSsrDuration,
    initialLoginInfoSls,
    isSelfView,
    terminalType: 'pc',
    accountId: Number((account as any)?.id),
    queryUid: router.query?.uid,
  });

  // 刷新全局 account：编辑资料成功后与 header 等区域保持一致
  const refreshGlobalAccount = useCallback(async () => {
    try {
      const latestAccount = await getUserLoginInfo(false, {}, Date.now());
      dispatch({
        namespace: namespace.app,
        type: ActionTypeValues.UPDATE_ACCOUNT,
        payload: latestAccount ? { ...latestAccount, status: PromiseState.FULFILLED } : latestAccount,
      });
    } catch (e) {
      return;
    }
  }, [dispatch]);

  // 积分显示逻辑：初始值来自 SSR / displayAccount，后续可被兑换回调实时改写
  const [points, setPoints] = useState<number>(userStats?.points || displayAccount?.credits || 0);
  // 超大积分显示上限处理
  const displayPoints = points > USER_CENTER_MAX_POINTS_VALUE ? `${USER_CENTER_MAX_POINTS_VALUE}+` : points;
  // 登录/访问奖励提示文案（仅展示一次）
  const creditTipText = resolveUserCenterCreditTip(initialUserInfo?.login_credit_added, initialUserInfo?.visit_credit_added);
  const displayName = displayAccount?.name || '';
  // 管理员模式下名称超长使用 tooltip，避免布局抖动
  const shouldShowAdminNameTooltip = isAdminView && displayName.length > 12;

  // 同步积分来源：优先 userStats，其次 displayAccount
  useEffect(() => {
    if (userStats?.points !== undefined) {
      setPoints(userStats.points);
    } else if (displayAccount?.credits !== undefined) {
      setPoints(displayAccount.credits);
    }
  }, [userStats?.points, displayAccount?.credits]);

  // 进入页时如果有积分奖励提示，弹 toast（一次性）
  useEffect(() => {
    if (!creditTipText || hasShownCreditTipRef.current) return;
    hasShownCreditTipRef.current = true;
    Toast.show({
      content: creditTipText,
      style: { top: '26%' },
      rootClassName: 'toastLineHeightUnset',
    });
  }, [creditTipText]);

  // 用户组 icon 变更后重置加载错误标记，允许重新渲染
  useEffect(() => {
    setBadgeIconError(false);
  }, [displayAccount?.group?.icon]);

  // 以 displayAccount 的 hide_distance 为准回填本地开关状态
  useEffect(() => {
    if (displayAccount?.hide_distance !== undefined) {
      setHideDistance(displayAccount.hide_distance);
    }
  }, [displayAccount?.hide_distance]);

  // 动作域逻辑下沉：侧栏、设备入口、发帖、里程开关、埋点
  const {
    handleToggleFlightVisibility,
    handlePost,
    handlePointsExchangeClick,
    handleDeviceIconClick,
    handleEditProfileClick,
    sidebarOptions,
    deviceCount,
    displayFlightDistance,
    adminUid,
    adminEmail,
    adminIp,
    adminLocation,
    adminLastLoginTime,
    previewDevices,
    showMoreDevices,
    showFlightInfo,
  } = useUserCenterPcActions({
    account,
    isSelfView,
    displayAccount,
    t,
    track,
    TRACK_EVENTS,
    permission,
    activeTab,
    saveTabContentScrollState: saveTabScrollY,
    setActiveTab,
    setShowPointsExchangeModal,
    setShowEditProfileModal,
    hideDistance,
    setHideDistance,
    updating,
    setUpdating,
  });

  // 访客页仅显示"帖子"tab；本人页显示完整四个 tab
  const visibleTabKeys = isSelfView
    ? ['myPosts', 'myReplies', 'myLikes', 'myCollections', 'messages']
    : ['myPosts'];

  // SEO 文案根据当前展示用户名动态拼接
  const seoTitle = t('user_center_meta_title', { name: displayAccount?.name || account?.name || '' });
  const seoDescription = t('user_center_meta_description', { name: displayAccount?.name || account?.name || '' });
  const seoKeywords = t('user_center_meta_keywords');

  // 页面只做装配：将渲染与交互透传给布局组件
  return (
    <UserCenterPcLayout
      t={t}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      seoKeywords={seoKeywords}
      account={account!}
      navList={navList || []}
      saltkey={saltkey || ''}
      isSelfView={isSelfView}
      isAdminView={isAdminView}
      displayAccount={displayAccount}
      displayName={displayName}
      shouldShowAdminNameTooltip={shouldShowAdminNameTooltip}
      badgeIconError={badgeIconError}
      setBadgeIconError={setBadgeIconError}
      handleEditProfileClick={handleEditProfileClick}
      showFlightInfo={showFlightInfo}
      displayFlightDistance={displayFlightDistance}
      hideDistance={hideDistance}
      handleToggleFlightVisibility={handleToggleFlightVisibility}
      handlePointsExchangeClick={handlePointsExchangeClick}
      displayPoints={displayPoints}
      deviceCount={deviceCount}
      previewDevices={previewDevices}
      showMoreDevices={showMoreDevices}
      handleDeviceIconClick={handleDeviceIconClick}
      setShowDevicesModal={setShowDevicesModal}
      adminUid={adminUid}
      adminEmail={adminEmail}
      adminIp={adminIp}
      adminLocation={adminLocation}
      adminLastLoginTime={adminLastLoginTime}
      sidebarOptions={sidebarOptions}
      activeTab={activeTab}
      handlePost={handlePost}
      visibleTabKeys={visibleTabKeys}
      renderTabPanel={(tabKey) =>
        renderPcTabContent({
          tabKey,
          isSelfView,
          t,
          showDrafts,
          showPosts,
          drafts,
          publishedPosts,
          postsLoading,
          postsHasMore,
          handleItemUpdate,
          likesInitialized,
          likesData,
          likesLoading,
          likesHasMore,
          collectionsInitialized,
          collectionsData,
          collectionsLoading,
          collectionsHasMore,
          repliesInitialized,
          repliesData,
          repliesLoading,
          messagesInitialized,
          messagesData,
          messagesLoading,
          setShowPointsExchangeModal,
        })
      }
      transparentMain={isPcTabContentEmpty({
        activeTab,
        showDrafts,
        showPosts,
        likesInitialized,
        likesData,
        likesLoading,
        collectionsInitialized,
        collectionsData,
        collectionsLoading,
        messagesInitialized,
        messagesData,
        messagesLoading,
      })}
      showDevicesModal={showDevicesModal}
      initialDevices={initialDevices}
      showPointsExchangeModal={showPointsExchangeModal}
      setShowPointsExchangeModal={setShowPointsExchangeModal}
      setPoints={setPoints}
      showEditProfileModal={showEditProfileModal}
      setShowEditProfileModal={setShowEditProfileModal}
      initialUserInfo={initialUserInfo}
      profilePatch={profilePatch}
      refreshGlobalAccount={refreshGlobalAccount}
      setProfilePatch={setProfilePatch}
      onCreditsChange={(newCredits) => {
        if (isSelfView) {
          setPoints(newCredits);
        }
      }}
    />
  );
};

// SSR 已下沉到 pc/ssr.ts，这里保持 next.js 约定导出
export const getServerSideProps = getUserCenterPcServerSideProps;

export default UserCenter;
